// @spec SUP-STR-017
'use client';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  QuestionnaireTemplateForm,
  useCreateTemplate,
} from '@/features/suppliers/admin/questionnaire-templates';
import type {
  CreateTemplateInput,
  UpdateTemplateInput,
} from '@/features/suppliers/admin/questionnaire-templates';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/suppliers/admin/questionnaire-templates/new')(
  {
    beforeLoad: () => ({
      getTitle: () => i18n.t('routeTitles.createQuestionnaireTemplate'),
    }),
    component: NewQuestionnaireTemplatePage,
  }
);

/**
 * Create new questionnaire template page.
 *
 * Provides the QuestionnaireTemplateForm component for creating new templates.
 * Users can:
 * - Set template metadata (name, description, category)
 * - Add sections and questions
 * - Configure scoring rules
 * - Save the template to the organization
 */
function NewQuestionnaireTemplatePage() {
  const { t } = useTranslation(['suppliers', 'common']);
  const navigate = useNavigate();
  const { organizations, isLoading: orgsLoading } = useOrganizations();
  const activeOrg = organizations.find((org) => org.isActive);

  const createTemplate = useCreateTemplate({
    onSuccess: (data) => {
      toast.success(t('suppliers:toast.templateCreatedSuccessfully'));
      void navigate({
        to: '/suppliers/admin/questionnaire-templates/$templateId',
        params: { templateId: data.id },
      });
    },
    onError: (error) => {
      toast.error(error.message || i18n.t('suppliers:toast.failedToCreateTemplate'));
    },
  });

  const handleSubmit = (data: CreateTemplateInput | UpdateTemplateInput): Promise<void> => {
    createTemplate.mutate(data as CreateTemplateInput);
    return Promise.resolve();
  };

  const handleCancel = () => {
    void navigate({ to: '/suppliers/admin/questionnaire-templates' });
  };

  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 rounded bg-muted" />
          <div className="h-64 w-96 rounded bg-muted" />
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="rounded-lg border border-dashed p-12 text-center">
          <h2 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noOrgSelected')}</h2>
          <p className="mt-2 text-muted-foreground">
            {t('common:routeErrors.noOrgSelectedDesc')}
          </p>
          <Button variant="outline" asChild className="mt-4">
            <Link to="/settings/organizations">{t('common:routeErrors.goToOrganizations')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl">
        <QuestionnaireTemplateForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isPending={createTemplate.isPending}
        />
      </div>
    </div>
  );
}
